/**
 * NotificationsPage — /notifications (admin-only).
 *
 * Four tabs:
 *   1. Log   — paginated notification table, failures surfaced first,
 *              status + type filters; clicking a row reveals the full body.
 *   2. Pipeline writes — audit trail of Task Pipeline stage writeback: what
 *              Sherlock changed on the Notion board (or would change, while
 *              writeback is disabled) and the ticket states behind it.
 *   3. Ruleset — active ruleset viewer / editor (admin can save a new version).
 *   4. Timeline — the stage-transition recorder's liveness (per-source last
 *              event + coverage %) plus per-task transition history with
 *              evidence, so a quiet recorder can't hide behind an empty page.
 *
 * Permission model:
 *   - The /notifications route is admin-only in the nav but accessible to
 *     any authenticated user directly. The page shows a read-only alert to
 *     non-admins (mirrors the Memory dashboard pattern).
 *   - The notification log itself returns 403 from the backend for non-admins;
 *     we surface that as an error state.
 *   - The ruleset GET is open to all; PUT is admin-only.
 */

import {
  Alert,
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useState, type ReactElement } from 'react';

import { isAdminEmail } from '@/auth/admin';
import { useAuth } from '@/contexts/AuthContext';
import { usePmNotifications } from '@/hooks/usePmNotifications';
import type { PmNotification, PmNotificationStatus } from '@/types/pm_notification';

import { PipelineWritesTab } from './PipelineWritesTab';
import { RulesetEditor } from './RulesetEditor';
import { TimelineTab } from './TimelineTab';

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

const STATUS_COLOR: Record<
  PmNotificationStatus,
  'error' | 'success' | 'default'
> = {
  failed: 'error',
  sent: 'success',
  skipped: 'default',
};

function StatusChip({ status }: { status: PmNotificationStatus }): ReactElement {
  return (
    <Chip
      label={status}
      color={STATUS_COLOR[status]}
      size="small"
      sx={{ fontWeight: 600, textTransform: 'lowercase' }}
    />
  );
}

// ---------------------------------------------------------------------------
// Notion link helper
// ---------------------------------------------------------------------------

function notionUrl(notionId: string): string {
  return `https://www.notion.so/${notionId.replaceAll('-', '')}`;
}

// ---------------------------------------------------------------------------
// Expandable table row
// ---------------------------------------------------------------------------

function NotificationRow({ row }: { row: PmNotification }): ReactElement {
  const [open, setOpen] = useState(false);

  const sentAt = new Date(row.sent_at).toLocaleString();
  const truncatedError =
    row.error && row.error.length > 80
      ? `${row.error.slice(0, 80)}…`
      : row.error;

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          '& > td': { borderBottom: open ? 0 : undefined },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ width: 40, p: 0.5 }}>
          <IconButton
            size="small"
            aria-label={open ? 'Collapse row' : 'Expand row to see body'}
          >
            {open ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            {sentAt}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{row.notification_type}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.destination}
          </Typography>
        </TableCell>
        <TableCell>
          {row.notion_id ? (
            <Tooltip title="Open in Notion">
              <IconButton
                component="a"
                href={notionUrl(row.notion_id)}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label={`Open Notion page ${row.notion_id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <StatusChip status={row.status as PmNotificationStatus} />
        </TableCell>
        <TableCell>
          {truncatedError ? (
            <Typography variant="body2" color="error.main" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {truncatedError}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ px: 4, py: 2, bgcolor: 'background.deeper' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Full body
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {row.body}
              </Box>
              {row.error && row.error.length > 80 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1.5, mb: 0.5, display: 'block' }}
                  >
                    Full error
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: 'error.light',
                      color: 'error.contrastText',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      overflowX: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {row.error}
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ---------------------------------------------------------------------------
// Log tab
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: Array<{ value: PmNotificationStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'failed', label: 'Failed' },
  { value: 'sent', label: 'Sent' },
  { value: 'skipped', label: 'Skipped' },
];

function LogTab(): ReactElement {
  const [statusFilter, setStatusFilter] = useState<PmNotificationStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = usePmNotifications({
    status: statusFilter || undefined,
    notification_type: typeFilter || undefined,
    page,
  });

  const rows = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <Box>
      {/* Filter bar */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as PmNotificationStatus | '');
              setPage(1);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="type-filter-label">Notification type</InputLabel>
          <Select
            labelId="type-filter-label"
            id="type-filter"
            label="Notification type"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All types</MenuItem>
            {/* These must match the backend notification_type values exactly —
                api/pm_notifications.py filters on equality, so a label that
                isn't a real pm_* value silently returns an empty page. */}
            <MenuItem value="pm_personal">pm_personal</MenuItem>
            <MenuItem value="pm_leads_overview">pm_leads_overview</MenuItem>
            <MenuItem value="pm_escalation">pm_escalation</MenuItem>
            <MenuItem value="pm_unowned">pm_unowned</MenuItem>
            <MenuItem value="pm_event_p0">pm_event_p0</MenuItem>
            <MenuItem value="pm_parking">pm_parking</MenuItem>
            <MenuItem value="pm_stage_mismatch">pm_stage_mismatch</MenuItem>
            <MenuItem value="pm_readiness">pm_readiness</MenuItem>
            <MenuItem value="pm_scorecard">pm_scorecard</MenuItem>
            <MenuItem value="pm_weekly_update">pm_weekly_update</MenuItem>
            <MenuItem value="pm_release_notes">pm_release_notes</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <IconButton size="large" aria-label="Refresh notification log" onClick={refetch}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {/* Error */}
      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading skeletons */}
      {isLoading && !data && (
        <Box role="status" aria-label="Loading notifications">
          {Array.from({ length: 6 }).map((_, i) => (
            // reason: stable static list — key-by-index is fine
            <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 0.5 }} />
          ))}
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && !error && rows.length === 0 && (
        <Box
          sx={{
            py: 8,
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No notifications match the current filters.
          </Typography>
        </Box>
      )}

      {/* Table */}
      {rows.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label="PM notification log">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Time (sent)</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <NotificationRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1}
          onPageChange={(_, newPage) => setPage(newPage + 1)}
          rowsPerPage={20}
          rowsPerPageOptions={[20]}
          aria-label="Notification log pagination"
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

type ActiveTab = 'log' | 'pipeline' | 'ruleset' | 'timeline';

export function NotificationsPage(): ReactElement {
  const [tab, setTab] = useState<ActiveTab>('log');
  const { user } = useAuth();
  const canEdit = isAdminEmail(user?.email);

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            color: 'primary.main',
          }}
        >
          notifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          PM delivery log and ruleset editor.
        </Typography>
      </Stack>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }} role="status">
          Read-only view. The notification log and ruleset edit actions are
          restricted to the admin (mohit@detectinspections.com).
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v: ActiveTab) => setTab(v)}
        aria-label="Notifications sections"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Log" value="log" />
        <Tab label="Pipeline writes" value="pipeline" />
        <Tab label="Ruleset" value="ruleset" />
        <Tab label="Timeline" value="timeline" />
      </Tabs>

      {tab === 'log' && (
        <Box role="tabpanel" aria-label="Notification log panel">
          <LogTab />
        </Box>
      )}
      {tab === 'pipeline' && (
        <Box role="tabpanel" aria-label="Pipeline writeback log panel">
          <PipelineWritesTab />
        </Box>
      )}
      {tab === 'ruleset' && (
        <Box role="tabpanel" aria-label="Ruleset editor panel">
          <RulesetEditor canEdit={canEdit} />
        </Box>
      )}
      {tab === 'timeline' && (
        <Box role="tabpanel" aria-label="Timeline panel">
          <TimelineTab canEdit={canEdit} />
        </Box>
      )}
    </Box>
  );
}
